import { useCallback, useEffect, useState } from "react";
import { fetchUser } from "../service/userService";
import { resolvePhotoUrl } from "../utils/profilePhoto";

const STORAGE_KEY = "inp_profile_draft";

export const DEFAULT_PROFILE = {
  memberId: "",
  firstName: "",
  middleName: "",
  lastName: "",
  personalEmail: "",
  emailVerified: false,
  workEmail: "",
  mobileNumber: "",
  dob: "",
  gender: "",
  pincode: "",
  city: "",
  state: "",
  country: "",
  highestEducation: "",
  annualEarnings: "",
  mailingAddress: "",
  skills: [],
  avatar: "",
  otherCountry: "",

  workStatus: "",
  employmentStatus: "",
  professionType: "",
  jobTitle: "",
  companyName: "",
  otherCompany: "",
  companyUrl: "",
  totalExperience: "",
  seniorityLevel: "",
  jobResponsibility: "",
  tenure: "",
  sector: "",
  otherSector: "",
  industry: "",
  otherIndustry: "",
  department: "",
  function: "",
  businessModel: "",
  companyRevenue: "",
  companyType: "",
  companySize: "",
  hqLocation: "",
  companyLinkedIn: "",
  aboutCompany: "",

  linkedin: "",
  facebook: "",
  instagram: "",
  x: "",
  aboutUser: "",

  previousEmployers: [],

  // Raw backend codes for lookup-based fields (gender, country, sector, ...).
  // Our UI only ever shows/edits the *_name display values, so these are kept
  // around purely to round-trip a field's real value back to /update-user
  // when the user hasn't changed it - without this, saving would silently
  // wipe out any coded field our form doesn't expose a picker for.
  apiCodes: {},
};

const readStoredProfile = (expectedEmail) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (expectedEmail && stored.personalEmail && stored.personalEmail.trim().toLowerCase() !== expectedEmail.trim().toLowerCase()) return null;
    return { ...DEFAULT_PROFILE, ...stored };
  } catch {
    return null;
  }
};

const splitSkills = (skillsString) =>
  (skillsString || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

// The API's dob is documented as a bare "YYYY-MM-DD", but this tolerates a
// trailing time component ("...T00:00:00.000Z") or other Date-parseable
// string too, so a slightly different real-world response still converts
// correctly instead of silently producing a garbled, unusable value.
const toDisplayDate = (rawDate) => {
  if (!rawDate) return "";

  const isoMatch = String(rawDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${d}/${m}/${y}`;
  }

  const parsed = new Date(rawDate);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getUTCFullYear();
    const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
    const d = String(parsed.getUTCDate()).padStart(2, "0");
    return `${d}/${m}/${y}`;
  }

  return "";
};

const toIsoDate = (displayDate) => {
  if (!displayDate) return null;
  const [d, m, y] = displayDate.split("/");
  return d && m && y ? `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}` : null;
};


// previous_employement: [{ company_name, job_title, from, to, location,
// job_description }, ...] - from/to are full ISO dates, not bare years.
const mapPreviousEmployers = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((emp, i) => ({
    id: `emp-api-${i}-${emp.company_name || ""}`,
    companyName: emp.company_name || "",
    jobTitle: emp.job_title || "",
    from: toDisplayDate(emp.from),
    to: toDisplayDate(emp.to),
    location: emp.location || "",
    description: emp.job_description || "",
  }));
};

// Maps the /api/user/fetch-user response's `data` object onto our internal
// profile shape.
export function mapApiUserToProfile(data) {
  if (!data) return {};

  return {
    memberId: data.member_id || "",
    firstName: data.first_name || "",
    middleName: data.middle_name || "",
    lastName: data.last_name || "",
    personalEmail: data.personal_email || "",
    emailVerified: Boolean(data.email_verified),
    workEmail: data.work_email || "",
    mobileNumber: data.mobile || "",
    dob: toDisplayDate(data.dob),
    gender: data.gender_name || "",
    pincode: data.zip_code || "",
    city: data.city || "",
    state: data.state_name || "",
    country: data.country_name || "",
    highestEducation: data.highest_education_name || "",
    annualEarnings: data.household_income_name || "",
    mailingAddress: data.mailing_address || "",
    skills: splitSkills(data.skills),
    avatar: resolvePhotoUrl(data.profile_photo, ""),
    otherCountry: data.other_country || "",

    workStatus: data.work_status_name || "",
    employmentStatus: data.employement_status_name || "",
    professionType: data.profession_type_name || "",
    jobTitle: data.job_title || "",
    companyName: data.current_company_name || "",
    otherCompany: data.other_company || "",
    companyUrl: data.company_url || "",
    totalExperience: data.work_experience_name || "",
    seniorityLevel: data.seniority_level_name || "",
    jobResponsibility: data.job_responsibility_name || data.job_responsibility || "",
    tenure: data.work_ex_c_company_name || "",
    sector: data.sector_name || "",
    otherSector: data.other_sector || "",
    industry: data.industry_name || "",
    otherIndustry: data.other_industry || "",
    department: data.department_name || "",
    function: data.function_name || "",
    businessModel: data.business_model_name || "",
    companyRevenue: data.company_revenue_name || "",
    companyType: data.company_type_name || "",
    companySize: data.company_size_name || "",
    hqLocation: data.headquarter || "",
    companyLinkedIn: data.company_linkedin || "",
    aboutCompany: data.about_company || "",

    linkedin: data.linkedin || "",
    facebook: data.facebook || "",
    instagram: data.insta || "",
    x: data.x || "",
    aboutUser: data.about_user || "",

    previousEmployers: mapPreviousEmployers(data.previous_employement),

    apiCodes: {
      gender: data.gender ?? "",
      country: data.country ?? "",
      state: data.state ?? "",
      highest_education: data.highest_education ?? "",
      household_income: data.household_income ?? "",
      work_status: data.work_status ?? "",
      employement_status: data.employement_status ?? "",
      profession_type: data.profession_type ?? "",
      work_experience: data.work_experience ?? "",
      seniority_level: data.seniority_level ?? "",
      job_responsibility: data.job_responsibility ?? "",
      work_ex_c_company: data.work_ex_c_company ?? "",
      sector: data.sector ?? "",
      industry: data.industry ?? "",
      department: data.department ?? "",
      function: data.function ?? "",
      company_name: data.company_name ?? "",
      business_model: data.business_model ?? "",
      company_revenue: data.company_revenue ?? "",
      company_type: data.company_type ?? "",
      company_size: data.company_size ?? "",
    },
  };
}

// Builds the /api/user/update-user request body from our internal profile
// shape. Free-text fields map straight across. Lookup-coded fields (country,
// sector, industry, education, ...) now send the real backend id captured
// when the user picked a value from that field's live options list - the
// dropdowns are backed by reference-data endpoints, not static guesses. Only
// gender has no such endpoint, so it still just round-trips whatever code
// was last fetched rather than resolving a freshly-picked display name.
export function buildUserUpdatePayload(profile) {
  const codes = profile.apiCodes || {};

  return {
    first_name: profile.firstName,
    middle_name: profile.middleName,
    last_name: profile.lastName,
    personal_email: profile.personalEmail,
    work_email: profile.workEmail,
    mobile: profile.mobileNumber,
    dob: toIsoDate(profile.dob),
    gender: codes.gender ?? "",
    zip_code: profile.pincode,
    country: codes.country ?? "",
    other_country: profile.otherCountry,
    state: codes.state ?? "",
    city: profile.city,
    mailing_address: profile.mailingAddress,
    highest_education: codes.highest_education ?? "",
    household_income: codes.household_income ?? "",
    skills: profile.skills.join(", "),
    work_status: codes.work_status ?? "",
    employement_status: codes.employement_status ?? "",
    profession_type: codes.profession_type ?? "",
    job_title: profile.jobTitle,
    work_experience: codes.work_experience ?? "",
    seniority_level: codes.seniority_level ?? "",
    job_responsibility: profile.jobResponsibility,
    work_ex_c_company: codes.work_ex_c_company ?? "",
    sector: codes.sector ?? "",
    other_sector: profile.otherSector,
    industry: codes.industry ?? "",
    other_industry: profile.otherIndustry,
    department: codes.department ?? "",
    function: codes.function ?? "",
    previous_employement: (() => {
      const filled = profile.previousEmployers.filter((emp) => emp.companyName && emp.jobTitle);
      return filled.length
        ? filled.map((emp) => ({
            company_name: emp.companyName,
            job_title: emp.jobTitle,
            from: toIsoDate(emp.from),
            to: toIsoDate(emp.to),
            location: emp.location,
            job_description: emp.description,
          }))
        : null;
    })(),
    linkedin: profile.linkedin,
    facebook: profile.facebook,
    insta: profile.instagram,
    x: profile.x,
    about_user: profile.aboutUser,
    company_name: codes.company_name ?? "",
    other_company: profile.otherCompany,
    company_url: profile.companyUrl,
    business_model: codes.business_model ?? "",
    company_revenue: codes.company_revenue ?? "",
    company_type: codes.company_type ?? "",
    company_size: codes.company_size ?? "",
    headquarter: profile.hqLocation,
    company_linkedin: profile.companyLinkedIn,
    about_company: profile.aboutCompany,
  };
}

export function useProfileData(seed) {
  const [profile, setProfile] = useState(() => readStoredProfile(seed?.email) || DEFAULT_PROFILE);
  const [apiCompletion, setApiCompletion] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);

  const loadFromApi = useCallback(async () => {
    if (!localStorage.getItem("token")) return;

    setApiLoading(true);
    try {
      const res = await fetchUser();
      setProfile((prev) => ({ ...prev, ...mapApiUserToProfile(res?.data) }));
      if (res?.profile_completion) setApiCompletion(res.profile_completion);
    } catch {
      // No live server data - keep whatever's already in local/seeded state.
    } finally {
      setApiLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFromApi();
  }, [loadFromApi]);

  useEffect(() => {
    if (!seed || (!seed.firstName && !seed.lastName && !seed.email)) return;

    setProfile((prev) => {
      if (seed.email && prev.personalEmail && prev.personalEmail.trim().toLowerCase() !== seed.email.trim().toLowerCase()) {
        return { ...DEFAULT_PROFILE, firstName: seed.firstName || "", lastName: seed.lastName || "", personalEmail: seed.email };
      }
      if (prev.firstName && prev.lastName && prev.personalEmail) return prev;
      return {
        ...prev,
        firstName: seed.firstName || prev.firstName,
        lastName: seed.lastName || prev.lastName,
        personalEmail: seed.email || prev.personalEmail,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.firstName, seed?.lastName, seed?.email]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  }, [profile]);

  const updateProfile = (patch) => {
    setProfile((prev) => ({ ...prev, ...patch }));
  };

  return { profile, setProfile, updateProfile, apiCompletion, apiLoading, refetch: loadFromApi };
}

// Both derived purely from the real /api/user/fetch-user response's
// `profile_completion` object - no local guessing. Until that call resolves
// (or if it fails), there's simply no completion data to show yet.
export function getProfileChecklist(apiCompletion) {
  if (!apiCompletion) return [];

  return [
    { key: "basicInfo", label: "Basic Information", complete: Boolean(apiCompletion.basic_information) },
    { key: "emailVerified", label: "Email Verified", complete: Boolean(apiCompletion.email_verified) },
    { key: "country", label: "Country Added", complete: Boolean(apiCompletion.country_added) },
    { key: "industry", label: "Industry Added", complete: Boolean(apiCompletion.industry_added) },
    { key: "education", label: "Education Details", complete: Boolean(apiCompletion.education_details) },
    { key: "career", label: "Career Details", complete: Boolean(apiCompletion.career_details) },
    { key: "organization", label: "Organization Details", complete: Boolean(apiCompletion.company_details) },
    { key: "previousEmployers", label: "Previous Employers", complete: Boolean(apiCompletion.previous_employer) },
    { key: "profilePhoto", label: "Profile Photo", complete: Boolean(apiCompletion.profile_photo) },
  ];
}

export function getProfileCompletion(apiCompletion) {
  const checklist = getProfileChecklist(apiCompletion);

  return {
    checklist,
    completedCount: checklist.filter((item) => item.complete).length,
    totalCount: checklist.length,
    percent: apiCompletion && typeof apiCompletion.percentage === "number" ? apiCompletion.percentage : null,
  };
}
